import { isDevMode } from '@angular/core';
import { Routes } from '@angular/router';
import { householdWorkspaceEditorGuard } from './guards/household-workspace-editor.guard';
import { unsavedAccountGuard } from './guards/unsaved-account.guard';
import { AUTH_REQUIRED, RoutePaths, dashboardChildPath } from './app.routes.paths';
import { accountChildGuard, accountGuard, guestGuard } from './guards/account.guard';
import { householdPermissions } from './models/dtos/households/household-dto';
import { MainPageLayoutComponent } from './page-layouts/main-page-layout/main-page-layout.component';

const emailLinkPage = () => import('./pages/auth/email-link-request/email-link-request-page.component')
  .then(module => module.EmailLinkRequestPageComponent);
const resetPage = () => import('./pages/reset-password/reset-password-page.component')
  .then(module => module.ResetPasswordPageComponent);
const loginLinkPage = () => import('./pages/login-with-token/login-with-token-page.component')
  .then(module => module.LoginWithTokenPageComponent);

export const routes: Routes = [
  {
    path: RoutePaths.dashboard,
    loadComponent: () => import('./page-layouts/workspace-layout/workspace-layout.component')
      .then(module => module.WorkspaceLayoutComponent),
    canActivate: [accountGuard],
    canActivateChild: [accountChildGuard],
    data: { [AUTH_REQUIRED]: true, workspaceArea: 'account' },
    children: [
      {
        path: '', pathMatch: 'full', title: 'Your workspace | Square Control',
        loadComponent: () => import('./pages/dashboard/dashboard-page.component')
          .then(module => module.DashboardPageComponent)
      },
      {
        path: dashboardChildPath(RoutePaths.dashboard_notifications), pathMatch: 'full', title: 'Notifications | Square Control',
        loadComponent: () => import('./pages/dashboard/notifications/notifications-page.component')
          .then(module => module.NotificationsPageComponent)
      },
      {
        path: 'account', pathMatch: 'full', title: 'My details | Square Control', canDeactivate: [unsavedAccountGuard],
        loadComponent: () => import('./pages/dashboard/account/profile/account-profile-page.component')
          .then(module => module.AccountProfilePageComponent)
      },
      {
        path: 'account/settings', title: 'Photo & address | Square Control', canDeactivate: [unsavedAccountGuard],
        loadComponent: () => import('./pages/dashboard/account/settings/account-settings-page.component')
          .then(module => module.AccountSettingsPageComponent)
      },
      {
        path: 'account/security', title: 'Password & security | Square Control', canDeactivate: [unsavedAccountGuard],
        loadComponent: () => import('./pages/dashboard/account/security/account-security-page.component')
          .then(module => module.AccountSecurityPageComponent)
      },
      {
        path: dashboardChildPath(RoutePaths.dashboard_households), pathMatch: 'full', title: 'Households | Square Control',
        loadComponent: () => import('./pages/dashboard/households/households-page.component')
          .then(module => module.HouseholdsPageComponent)
      },
      {
        path: dashboardChildPath(RoutePaths.dashboard_household_with_id),
        loadComponent: () => import('./page-layouts/household-workspace-layout/household-workspace-layout.component')
          .then(module => module.HouseholdWorkspaceLayoutComponent),
        children: [
          {
            path: '', pathMatch: 'full', title: 'Household overview | Square Control',
            data: { householdBreadcrumbs: [{ label: 'Overview' }] },
            loadComponent: () => import('./pages/dashboard/households/household-workspace-overview/household-workspace-overview-page.component')
              .then(module => module.HouseholdWorkspaceOverviewPageComponent)
          },
          {
            path: 'children', pathMatch: 'full', title: 'Household children | Square Control',
            data: { householdPermission: householdPermissions.fullMember, householdBreadcrumbs: [{ label: 'Children' }] },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/account/children/children-page.component')
              .then(module => module.ChildrenPageComponent)
          },
          {
            path: 'children/:childAccountId/settings', pathMatch: 'full', title: 'Child’s photo & address | Square Control',
            data: {
              childProfile: true,
              householdPermission: householdPermissions.fullMember,
              householdBreadcrumbs: [
                { label: 'Children', path: 'children' },
                { label: 'Child’s details', path: 'children/:childAccountId' },
                { label: 'Photo & address' }
              ]
            },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/account/settings/account-settings-page.component')
              .then(module => module.AccountSettingsPageComponent)
          },
          {
            path: 'children/:childAccountId', pathMatch: 'full', title: 'Child’s details | Square Control',
            data: {
              childProfile: true,
              householdPermission: householdPermissions.fullMember,
              householdBreadcrumbs: [{ label: 'Children', path: 'children' }, { label: 'Child’s details' }]
            },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/account/profile/account-profile-page.component')
              .then(module => module.AccountProfilePageComponent)
          },
          {
            path: 'sessions', pathMatch: 'full', title: 'Household sessions | Square Control',
            data: {
              householdPermission: [householdPermissions.fullMember, householdPermissions.sessionManager],
              householdBreadcrumbs: [{ label: 'Sessions' }]
            },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/account/sessions/account-sessions-page.component')
              .then(module => module.AccountSessionsPageComponent)
          },
          {
            path: 'registrations', pathMatch: 'full', title: 'Household registrations | Square Control',
            data: { householdPermission: householdPermissions.fullMember, householdBreadcrumbs: [{ label: 'Registrations' }] },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/account/registrations/account-registrations-page.component')
              .then(module => module.AccountRegistrationsPageComponent)
          },
          {
            path: 'registrations/payment/:submissionId', pathMatch: 'full', title: 'Household registration payment | Square Control',
            data: {
              householdPermission: householdPermissions.fullMember,
              householdBreadcrumbs: [{ label: 'Registrations', path: 'registrations' }, { label: 'Payment' }]
            },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/account/registrations/payment/registration-payment-page.component')
              .then(module => module.RegistrationPaymentPageComponent)
          },
          {
            path: 'invoices', pathMatch: 'full', title: 'Household invoices | Square Control',
            data: { householdPermission: householdPermissions.fullMember, householdBreadcrumbs: [{ label: 'Invoices' }] },
            loadComponent: () => import('./pages/dashboard/invoices/account-invoices-page.component')
              .then(module => module.AccountInvoicesPageComponent)
          },
          {
            path: 'invoices/:invoiceId', pathMatch: 'full', title: 'Household invoice details | Square Control',
            data: {
              householdPermission: householdPermissions.fullMember,
              householdBreadcrumbs: [{ label: 'Invoices', path: 'invoices' }, { label: 'Invoice details' }]
            },
            loadComponent: () => import('./pages/dashboard/invoices/invoice-details/account-invoice-details-page.component')
              .then(module => module.AccountInvoiceDetailsPageComponent)
          },
          {
            path: 'members', pathMatch: 'full', title: 'Household members | Square Control',
            data: { householdBreadcrumbs: [{ label: 'Members' }] },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/households/household-members/household-members-page.component')
              .then(module => module.HouseholdMembersPageComponent)
          },
          {
            path: 'settings', pathMatch: 'full', title: 'Household settings | Square Control',
            data: {
              householdPermission: [householdPermissions.owner, householdPermissions.fullMember],
              householdBreadcrumbs: [{ label: 'Settings' }]
            },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/households/household-settings/household-settings-page.component')
              .then(module => module.HouseholdSettingsPageComponent)
          },
          {
            path: 'invites', pathMatch: 'full', title: 'Household invitations | Square Control',
            data: { householdPermission: householdPermissions.owner, householdBreadcrumbs: [{ label: 'Invitations' }] },
            canDeactivate: [householdWorkspaceEditorGuard],
            loadComponent: () => import('./pages/dashboard/households/household-invitations-page.component')
              .then(module => module.HouseholdInvitationsPageComponent)
          }
        ]
      },
      {
        path: 'account/registrations', pathMatch: 'full', canDeactivate: [unsavedAccountGuard], title: 'Your registrations | Square Control',
        loadComponent: () => import('./pages/dashboard/account/registrations/account-registrations-page.component')
          .then(module => module.AccountRegistrationsPageComponent)
      },
      {
        path: 'account/registrations/payment/:submissionId', pathMatch: 'full', title: 'Registration payment | Square Control',
        canDeactivate: [unsavedAccountGuard],
        loadComponent: () => import('./pages/dashboard/account/registrations/payment/registration-payment-page.component')
          .then(module => module.RegistrationPaymentPageComponent)
      },
      {
        path: 'account/sessions', pathMatch: 'full', canDeactivate: [unsavedAccountGuard], title: 'Your sessions | Square Control',
        loadComponent: () => import('./pages/dashboard/account/sessions/account-sessions-page.component')
          .then(module => module.AccountSessionsPageComponent)
      },
      {
        path: 'account/clubs', pathMatch: 'full', title: 'Clubs I’m a member of | Square Control', data: { clubView: 'memberships' },
        loadComponent: () => import('./pages/dashboard/clubs/club-directory-page.component')
          .then(module => module.ClubDirectoryPageComponent)
      },
      {
        path: 'clubs', pathMatch: 'full', title: 'Clubs I work at | Square Control', data: { clubView: 'managed', workspaceArea: 'club' },
        loadComponent: () => import('./pages/dashboard/clubs/club-directory-page.component')
          .then(module => module.ClubDirectoryPageComponent)
      },
      {
        path: dashboardChildPath(RoutePaths.dashboard_club_create), pathMatch: 'full', title: 'Create club | Square Control',
        data: { workspaceArea: 'club' },
        canDeactivate: [unsavedAccountGuard],
        loadComponent: () => import('./pages/dashboard/clubs/club-create/club-create-page.component')
          .then(module => module.ClubCreatePageComponent)
      },
      {
        path: 'clubs/:clubId', title: 'Club workspace | Square Control',
        data: { workspaceArea: 'club' },
        loadComponent: () => import('./page-layouts/club-workspace-layout/club-workspace-layout.component')
          .then(module => module.ClubWorkspaceLayoutComponent),
        children: [
          { path: '', pathMatch: 'full', title: 'Club overview | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Overview' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-workspace-overview/club-workspace-overview-page.component')
              .then(module => module.ClubWorkspaceOverviewPageComponent) },
          { path: 'edit', pathMatch: 'full', title: 'Edit club | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Overview', path: '' }, { label: 'Edit club' }] },
            canDeactivate: [unsavedAccountGuard],
            loadComponent: () => import('./pages/dashboard/clubs/club-edit/club-edit-page.component')
              .then(module => module.ClubEditPageComponent) },
          { path: 'payments', pathMatch: 'full', title: 'Payment setup | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Payment setup' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-payment-setup/club-payment-setup-page.component')
              .then(module => module.ClubPaymentSetupPageComponent) },
          { path: 'payment-providers/setup/hook/:paymentProviderId/:path', pathMatch: 'full', title: 'Stripe connection | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Payment setup', path: 'payments' }, { label: 'Stripe connection' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-payment-setup/stripe-return/club-stripe-return-page.component')
              .then(module => module.ClubStripeReturnPageComponent) },
          { path: 'invoices', pathMatch: 'full', title: 'Club invoices | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Invoices' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-invoices/club-invoices-page.component')
              .then(module => module.ClubInvoicesPageComponent) },
          { path: 'invoices/:invoiceId', pathMatch: 'full', title: 'Club invoice details | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Invoices', path: 'invoices' }, { label: 'Invoice details' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-invoices/club-invoice-details/club-invoice-details-page.component')
              .then(module => module.ClubInvoiceDetailsPageComponent) },
          { path: 'staff', pathMatch: 'full', title: 'Club staff | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Staff' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-staff/club-staff-page.component')
              .then(module => module.ClubStaffPageComponent) },
          { path: 'staff/invitations', pathMatch: 'full', title: 'Staff invitations | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Staff', path: 'staff' }, { label: 'Invitations' }] },
            canDeactivate: [unsavedAccountGuard],
            loadComponent: () => import('./pages/dashboard/clubs/club-staff/club-staff-invitations/club-staff-invitations-page.component')
              .then(module => module.ClubStaffInvitationsPageComponent) },
          { path: 'sessions', pathMatch: 'full', title: 'Club sessions | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Sessions' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-sessions/club-sessions-page.component')
              .then(module => module.ClubSessionsPageComponent) },
          { path: 'sessions/:sessionId', pathMatch: 'full', title: 'Class register | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Sessions', path: 'sessions' }, { label: 'Register' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-sessions/club-register/club-register-page.component')
              .then(module => module.ClubRegisterPageComponent) },
          { path: 'students', pathMatch: 'full', title: 'Club students | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Students' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-students/club-students-page.component')
              .then(module => module.ClubStudentsPageComponent) },
          { path: 'classes', pathMatch: 'full', title: 'Club classes | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Classes' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-classes/club-classes-page.component')
              .then(module => module.ClubClassesPageComponent) },
          { path: 'classes/create', pathMatch: 'full', title: 'Create class | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Classes', path: 'classes' }, { label: 'Create class' }] },
            canDeactivate: [unsavedAccountGuard],
            loadComponent: () => import('./pages/dashboard/clubs/club-classes/club-class-editor-page.component')
              .then(module => module.ClubClassEditorPageComponent) },
          { path: 'classes/:classId/edit', pathMatch: 'full', title: 'Edit class | Square Control',
            data: { clubBreadcrumbs: [
              { label: 'Classes', path: 'classes' },
              { label: 'Class details', path: 'classes/:classId' },
              { label: 'Edit class' }
            ] },
            canDeactivate: [unsavedAccountGuard],
            loadComponent: () => import('./pages/dashboard/clubs/club-classes/club-class-editor-page.component')
              .then(module => module.ClubClassEditorPageComponent) },
          { path: 'classes/:classId', pathMatch: 'full', title: 'Class details | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Classes', path: 'classes' }, { label: 'Class details' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-classes/club-class-details-page.component')
              .then(module => module.ClubClassDetailsPageComponent) },
          { path: 'terms', pathMatch: 'full', title: 'Club terms | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Terms' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-terms/club-terms-page.component')
              .then(module => module.ClubTermsPageComponent) },
          { path: 'terms/create', pathMatch: 'full', title: 'Create term | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Terms', path: 'terms' }, { label: 'Create term' }] },
            canDeactivate: [unsavedAccountGuard],
            loadComponent: () => import('./pages/dashboard/clubs/club-terms/club-term-create/club-term-create-page.component')
              .then(module => module.ClubTermCreatePageComponent) },
          { path: 'terms/:termId/edit', pathMatch: 'full', title: 'Edit term | Square Control',
            data: { clubBreadcrumbs: [
              { label: 'Terms', path: 'terms' },
              { label: 'Term details', path: 'terms/:termId' },
              { label: 'Edit term' }
            ] },
            canDeactivate: [unsavedAccountGuard],
            loadComponent: () => import('./pages/dashboard/clubs/club-terms/club-term-create/club-term-create-page.component')
              .then(module => module.ClubTermCreatePageComponent) },
          { path: 'terms/:termId', pathMatch: 'full', title: 'Term details | Square Control',
            data: { clubBreadcrumbs: [{ label: 'Terms', path: 'terms' }, { label: 'Term details' }] },
            loadComponent: () => import('./pages/dashboard/clubs/club-terms/club-term-details-page.component')
              .then(module => module.ClubTermDetailsPageComponent) },
          { path: 'terms/:termId/invitations/create', pathMatch: 'full', title: 'Invite a student | Square Control',
            data: { clubBreadcrumbs: [
              { label: 'Terms', path: 'terms' },
              { label: 'Term details', path: 'terms/:termId' },
              { label: 'Invite students' }
            ] },
            canDeactivate: [unsavedAccountGuard],
            loadComponent: () => import('./pages/dashboard/clubs/club-terms/club-term-invitations/club-term-invitations-page.component')
              .then(module => module.ClubTermInvitationsPageComponent) },
          { path: 'terms/:termId/registrations', pathMatch: 'full', canDeactivate: [unsavedAccountGuard], title: 'Term registrations | Square Control',
            data: { clubBreadcrumbs: [
              { label: 'Terms', path: 'terms' },
              { label: 'Term details', path: 'terms/:termId' },
              { label: 'Registrations' }
            ] },
            loadComponent: () => import('./pages/dashboard/clubs/club-terms/club-term-registrations/club-term-registrations-page.component')
              .then(module => module.ClubTermRegistrationsPageComponent) }
        ]
      },
      { path: '**', title: 'Page not found | Square Control',
        loadComponent: () => import('./pages/not-found/not-found-page.component')
          .then(module => module.NotFoundPageComponent) }
    ]
  },
  {
    path: RoutePaths.root,
    component: MainPageLayoutComponent,
    children: [
      {
        path: RoutePaths.root, pathMatch: 'full', title: 'Chess clubs, registrations and sessions | Square Control',
        loadComponent: () => import('./pages/home/home-page.component')
          .then(module => module.HomePageComponent)
      },
      {
        path: RoutePaths.players_and_parents, pathMatch: 'full', title: 'Players and parents | Square Control',
        loadComponent: () => import('./pages/players-and-parents/players-and-parents-page.component')
          .then(module => module.PlayersAndParentsPageComponent)
      },
      {
        path: RoutePaths.club_owners_and_teachers, pathMatch: 'full', title: 'Club owners and teachers | Square Control',
        loadComponent: () => import('./pages/club-owners-and-teachers/club-owners-and-teachers-page.component')
          .then(module => module.ClubOwnersAndTeachersPageComponent)
      },
      {
        path: RoutePaths.login, pathMatch: 'full', title: 'Sign in | Square Control', canActivate: [guestGuard],
        loadComponent: () => import('./pages/login/login-page.component')
          .then(module => module.LoginPageComponent)
      },
      {
        path: RoutePaths.register, title: 'Create account | Square Control', canActivate: [guestGuard],
        loadComponent: () => import('./pages/register/register-page.component')
          .then(module => module.RegisterPageComponent)
      },
      {
        path: RoutePaths.onboarding, pathMatch: 'full', title: 'Get started | Square Control',
        canActivate: [accountGuard], canDeactivate: [unsavedAccountGuard], data: { [AUTH_REQUIRED]: true },
        loadComponent: () => import('./pages/onboarding/onboarding-page.component')
          .then(module => module.OnboardingPageComponent)
      },
      {
        path: 'login/session', title: 'Reconnect | Square Control',
        loadComponent: () => import('./pages/auth/session-recovery/session-recovery-page.component')
          .then(module => module.SessionRecoveryPageComponent)
      },
      {
        path: RoutePaths.reset_password, pathMatch: 'full', title: 'Reset password | Square Control',
        loadComponent: emailLinkPage, data: { linkKind: 'reset' }
      },
      { path: RoutePaths.reset_password_action, title: 'Reset password | Square Control', loadComponent: resetPage },
      { path: 'login/reset-password/:token', title: 'Reset password | Square Control', loadComponent: resetPage },
      {
        path: RoutePaths.login_with_token, pathMatch: 'full', title: 'Email sign-in | Square Control',
        loadComponent: emailLinkPage, data: { linkKind: 'login' }
      },
      { path: RoutePaths.login_with_token_action, title: 'Email sign-in | Square Control', loadComponent: loginLinkPage },
      { path: 'login/login-with-token/:token', title: 'Email sign-in | Square Control', loadComponent: loginLinkPage },
      {
        path: RoutePaths.clubs, pathMatch: 'full', title: 'Find a club | Square Control',
        loadComponent: () => import('./pages/clubs/find-club/find-club-page.component')
          .then(module => module.FindClubPageComponent)
      },
      {
        path: RoutePaths.public_term_details, pathMatch: 'full', title: 'Term details | Square Control',
        loadComponent: () => import('./pages/clubs/term-details/public-term-details-page.component')
          .then(module => module.PublicTermDetailsPageComponent)
      },
      {
        path: RoutePaths.register_club_term, title: 'Term registration | Square Control',
        canActivate: [accountGuard], canDeactivate: [unsavedAccountGuard], data: { [AUTH_REQUIRED]: true },
        loadComponent: () => import('./pages/clubs/term-registration/term-registration-page.component')
          .then(module => module.TermRegistrationPageComponent)
      },
      {
        path: RoutePaths.club_with_id, title: 'Club details | Square Control',
        loadComponent: () => import('./pages/clubs/club-details/public-club-details-page.component')
          .then(module => module.PublicClubDetailsPageComponent)
      },
      {
        path: RoutePaths.household_invite, title: 'Household invitation | Square Control',
        loadComponent: () => import('./pages/invites/household-invitation-page.component')
          .then(module => module.HouseholdInvitationPageComponent)
      },
      {
        path: RoutePaths.invites_action, title: 'Term invitation | Square Control', canDeactivate: [unsavedAccountGuard],
        loadComponent: () => import('./pages/invites/term-invitation-page.component')
          .then(module => module.TermInvitationPageComponent)
      },
      {
        path: RoutePaths.invites, title: 'Term invitation | Square Control', canDeactivate: [unsavedAccountGuard],
        loadComponent: () => import('./pages/invites/term-invitation-page.component')
          .then(module => module.TermInvitationPageComponent)
      },
      {
        path: 'design-system', canMatch: [() => isDevMode()], title: 'Clarity components | Square Control',
        loadComponent: () => import('./pages/design-system/design-system-page.component')
          .then(module => module.DesignSystemPageComponent)
      },
      {
        path: '**', title: 'Page not found | Square Control',
        loadComponent: () => import('./pages/not-found/not-found-page.component')
          .then(module => module.NotFoundPageComponent)
      }
    ]
  }
];
